// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentCitationLinks.test.tsx
import { ArrowSquareOut } from "@phosphor-icons/react";
import { Button, Dialog, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import {
  CITATION_CHIP_CLASS,
  CITATION_DIALOG_CONTENT_CLASS,
} from "./citationChipStyles";

import { Preview } from "../../../../components/Preview";
import {
  type TicketMessageCitation,
  isAgentSourceCitation,
} from "../../utils/commentSourceUtils";

/** An agent naming its own source by id rather than by URL.
 *
 * Renders the quote the agent carried and the source it named. It does NOT
 * fabricate a link: `sourceId` is opaque here, and its route depends on the
 * `source` kind the agent chose. A chip that says what it rests on beats the
 * inert "Source" + warning this variant used to render, and beats a link that
 * lands on a 404. */
export function AgentSourceCitationLink({
  citation,
}: {
  citation: TicketMessageCitation;
}) {
  const { t } = useTranslation("ui");
  if (!isAgentSourceCitation(citation)) return null;
  const label = citation.source || t("commentCitationLinks.source");

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <button
          type="button"
          data-testid={`comment-panel-citation-agent-${citation.sourceId}`}
          aria-label={t("commentCitationLinks.agentChipAriaLabel", { label })}
          className={CITATION_CHIP_CLASS}
        >
          <Text as="span" size="1" weight="medium">
            {label}
          </Text>
          <ArrowSquareOut size={12} aria-hidden="true" />
        </button>
      </Dialog.Trigger>
      <Dialog.Content
        data-testid="comment-citation-agent-dialog"
        className={CITATION_DIALOG_CONTENT_CLASS}
      >
        <Dialog.Title>{label}</Dialog.Title>
        <Dialog.Description className="hidden">
          {t("commentCitationLinks.agentSourceExcerpt")}
        </Dialog.Description>
        {citation.quote ? (
          <Preview text={citation.quote} />
        ) : (
          <Text size="2" className="text-puntt-neutral-gray-11">
            {t("commentCitationLinks.noExcerpt")}
          </Text>
        )}
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            {t("commentCitationLinks.close")}
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}
